import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import packageJson from '../package.json';
import Dashboard from './components/Dashboard';
import Layout from './components/Layout';
import Calendar from './components/Calendar';
import Members from './components/Members';
import Login from './components/Login';
import Register from './components/Register';
import VerifyCode from './components/VerifyCode';
import ForgotPassword from './components/ForgotPassword';
import AccountStatements from './pages/AccountStatements';
import DocumentManagement from './pages/DocumentManagement';
import AiUsage from './pages/AiUsage';
import AccountOverview from './pages/AccountOverviewEnhanced';
import MyDrinks from './pages/MyDrinks';
import DrinksManagement from './pages/DrinksManagement';
import DogManagement from './pages/DogManagement';
import DogManagementBeta from './pages/DogManagementBeta';
import AdminConfig from './pages/AdminConfig';
import AdminFinanceExport from './pages/AdminFinanceExport';
import AdminAnlagen from './pages/AdminAnlagen';
import AdminLogs from './pages/AdminLogs';
import AdminEmailProtokoll from './pages/AdminEmailProtokoll';
import AdminBackupStatus from './pages/AdminBackupStatus';
import AdminKassenpruefer from './pages/AdminKassenpruefer';
import AdminStructure from './pages/AdminStructure';
import GuestCardManagement from './pages/GuestCardManagement';
import VerzehrkostenBeta from './pages/VerzehrkostenBeta';
import MyGuestCards from './pages/MyGuestCards';
import MemberStatistics from './pages/MemberStatistics';
import PrivacyManagement from './pages/PrivacyManagement';
import MyDogs from './components/MyDogs';
import Profile from './pages/Profile';
import BudgetPlanning from './pages/BudgetPlanning';
import MyBookings from './pages/MyBookings';
import PayPalPayments from './components/PayPalPayments';
import PayPalSuccess from './pages/PayPalSuccess';
import PayPalCancel from './pages/PayPalCancel';
import PayPalSetupGuide from './pages/PayPalSetupGuide';
import ProjectsManagement from './pages/ProjectsManagement';
import WaitlistManagement from './pages/WaitlistManagement';
import MyProjects from './pages/MyProjects';
import ComponentsDemo from './pages/ComponentsDemo';
import Kassenpruefer from './pages/Kassenpruefer';
import MyEvents from './pages/MyEvents';
import EventManagement from './pages/EventManagement';
import UserActivityAnalytics from './pages/UserActivityAnalytics';
import apiClient from './utils/apiClient';
import { getPrimaryRole, ROLES } from './utils/roleUtils';
import './index.css';

// Auth Context with BASE_URL
export const AuthContext = React.createContext<any>(null);

// Rollen-Debug: nur aktiv, wenn REACT_APP_ROLE_DEBUG=true (lokale Entwicklung)
const ROLE_DEBUG_ENABLED = process.env.REACT_APP_ROLE_DEBUG === 'true';

function App() {
  const [user, setUser] = useState<any>(null);
  // Override-Rolle für Debug-Ansicht (null = echte Rolle)
  const [debugRole, setDebugRole] = useState<string | null>(
    ROLE_DEBUG_ENABLED ? localStorage.getItem('debug_role') : null
  );
  const [darkMode, setDarkMode] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const [frontendVersion, setFrontendVersion] = useState(packageJson.version);
  const [backendVersion, setBackendVersion] = useState('');
  const [BASE_URL, setBASE_URL] = useState<string>(
    localStorage.getItem('club_url') ||
    process.env.REACT_APP_API_URL ||
    'http://localhost:4000'
  );

  useEffect(() => {
    // Check for saved dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }

    // Check for existing token on mount
    checkAuthToken();
  }, []);

  const checkAuthToken = async () => {
    const token = localStorage.getItem('jwt_token');
    const clubUrl = localStorage.getItem('club_url');
    
    if (token && clubUrl) {
      try {
        // Set the base URL before verification
        setBASE_URL(clubUrl);
        
        // Verify token with backend (POST request required)
        const response = await apiClient.post('/api/app-auth/verify-token', {});
        if (response.data.success && response.data.user) {
          setUser(response.data.user);
          // Cache the verified user data
          localStorage.setItem('cached_user', JSON.stringify(response.data.user));
          console.log('[App] Token valid, user:', response.data.user.email);
        } else if (response.data.success === false) {
          // Only clear if explicitly invalid
          console.log('[App] Token explicitly invalid, clearing...');
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('club_url');
          localStorage.removeItem('club_id');
          localStorage.removeItem('club_name');
          localStorage.removeItem('cached_user');
        }
      } catch (error: any) {
        console.error('[App] Token verification failed:', error);
        
        // Check if it's a network error or server error
        if (error.response?.status === 401) {
          // Only clear token for 401 errors
          console.log('[App] Token unauthorized, clearing...');
          localStorage.removeItem('jwt_token');
          localStorage.removeItem('club_url');
          localStorage.removeItem('club_id');
          localStorage.removeItem('club_name');
          localStorage.removeItem('cached_user');
        } else if (!error.response) {
          // Network error - don't clear token, might be temporary
          console.log('[App] Network error during verification, keeping token');
          // Try to use cached user data if available
          const cachedUser = localStorage.getItem('cached_user');
          if (cachedUser) {
            try {
              setUser(JSON.parse(cachedUser));
              setBASE_URL(clubUrl);
              console.log('[App] Using cached user data');
            } catch (e) {
              console.error('[App] Failed to parse cached user');
            }
          }
        }
      }
    }
    
    setIsCheckingToken(false);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', String(newDarkMode));
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const login = async (email: string, password: string, serverUrl?: string) => {
    try {
      const loginUrl = serverUrl || BASE_URL;
      
      // Login with required parameters for backend
      const response = await apiClient.post('/api/app-auth/login', {
        email,
        password,
        platform: 'web',  // Backend expects 'platform' instead of 'device'
        version: '3.10.0'
      }, {
        baseURL: loginUrl
      });

      if (response.data.token) {
        // Save token and club URL
        localStorage.setItem('jwt_token', response.data.token);
        localStorage.setItem('club_url', loginUrl);
        
        // Cache user data for offline/network error scenarios
        if (response.data.user) {
          localStorage.setItem('cached_user', JSON.stringify(response.data.user));
        }
        
        // Update state with user data
        setBASE_URL(loginUrl);
        setUser(response.data.user);
        
        console.log('[App] Login successful:', response.data.user.email);
        return { success: true };
      }
      
      return { success: false, error: 'Login fehlgeschlagen' };
    } catch (error: any) {
      console.error('[App] Login error:', error.response?.data || error.message);
      
      // For demo mode, create mock user
      if (email && password) {
        const mockUser = {
          mg_nr: '12345',
          mitglied_nr: '12345',
          vorname: email.split('@')[0],
          nachname: 'Demo',
          email: email,
          vorstand: email.includes('vorstand') ? 1 : 0,
          trainer: email.includes('trainer') ? 1 : 0,
        };
        
        // Mock token
        const mockToken = btoa(JSON.stringify({ email, timestamp: Date.now() }));
        localStorage.setItem('jwt_token', mockToken);
        localStorage.setItem('club_url', serverUrl || BASE_URL);
        
        setUser(mockUser);
        setBASE_URL(serverUrl || BASE_URL);
        
        return { success: true };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.message || 'Verbindungsfehler' 
      };
    }
  };

  const logout = () => {
    // Clear all auth data
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('club_url');
    localStorage.removeItem('club_id');
    localStorage.removeItem('club_name');
    localStorage.removeItem('cached_user');
    setUser(null);
  };

  // Helper function to determine default route based on user role
  const getDefaultRoute = (user: any): string => {
    if (!user) return '/login';
    
    const primaryRole = getPrimaryRole(effectiveUser);
    
    switch(primaryRole) {
      case ROLES.BOARD:
        return '/dashboard';
      case ROLES.TRAINER:
      case ROLES.MEMBER:
      case ROLES.GUEST:
        return '/calendar';
      default:
        return '/calendar';
    }
  };

  // Debug-Rolle setzen und persistieren (nur bei aktivem Debug-Flag)
  const applyDebugRole = (role: string | null) => {
    if (!ROLE_DEBUG_ENABLED) return;
    setDebugRole(role);
    if (role) {
      localStorage.setItem('debug_role', role);
    } else {
      localStorage.removeItem('debug_role');
    }
  };

  // Effektiver User: im Debug-Modus wird die Rolle überschrieben, sonst der echte User.
  // Wir überschreiben nur das roles-Array – roleUtils wertet dieses vorrangig aus,
  // sodass Navigation, Routing und Anzeige die gewählte Rolle simulieren.
  const effectiveUser = React.useMemo(() => {
    if (!ROLE_DEBUG_ENABLED || !debugRole || !user) return user;
    return {
      ...user,
      roles: [{ name: debugRole, description: debugRole, priority: 100 }],
      __debugRole: debugRole,
    };
  }, [user, debugRole]);

  // Show loading while checking token
  if (isCheckingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-background-dark">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">Prüfe Authentifizierung...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      user: effectiveUser,
      realUser: user,
      setUser,
      login,
      logout,
      darkMode,
      toggleDarkMode,
      BASE_URL,
      setBASE_URL,
      isCheckingToken,
      frontendVersion,
      backendVersion,
      roleDebugEnabled: ROLE_DEBUG_ENABLED,
      debugRole,
      setDebugRole: applyDebugRole
    }}>
      <Router>
        <Routes>
          <Route path="/login" element={user ? <Navigate to={getDefaultRoute(effectiveUser)} /> : <Login />} />
          <Route path="/register" element={user ? <Navigate to={getDefaultRoute(effectiveUser)} /> : <Register />} />
          <Route path="/verify-code" element={user ? <Navigate to={getDefaultRoute(effectiveUser)} /> : <VerifyCode />} />
          <Route path="/forgot-password" element={user ? <Navigate to={getDefaultRoute(effectiveUser)} /> : <ForgotPassword />} />
          <Route path="/" element={user ? <Layout /> : <Navigate to="/login" replace />}>
            <Route index element={user ? <Navigate to={getDefaultRoute(effectiveUser)} replace /> : <Navigate to="/login" replace />} />
            
            {/* Board only routes */}
            <Route path="dashboard" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <Dashboard /> : <Navigate to="/calendar" />} />
            <Route path="members" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <Members /> : <Navigate to="/calendar" />} />
            <Route path="dog-management" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <DogManagement /> : <Navigate to="/calendar" />} />
            <Route path="dog-management-beta" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <DogManagementBeta /> : <Navigate to="/calendar" />} />
            <Route path="drinks-management" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <DrinksManagement /> : <Navigate to="/calendar" />} />
            <Route path="account-statements" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AccountStatements /> : <Navigate to="/calendar" />} />
            <Route path="account-overview" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AccountOverview /> : <Navigate to="/calendar" />} />
            <Route path="documents" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <DocumentManagement /> : <Navigate to="/calendar" />} />
            <Route path="ki-nutzung" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AiUsage /> : <Navigate to="/calendar" />} />
            <Route path="budget-planning" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <BudgetPlanning /> : <Navigate to="/calendar" />} />
            <Route path="paypal-payments" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <PayPalPayments /> : <Navigate to="/calendar" />} />
            <Route path="admin/config" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AdminConfig /> : <Navigate to="/calendar" />} />
            <Route path="admin/kassenpruefer" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AdminKassenpruefer /> : <Navigate to="/calendar" />} />
            <Route path="admin/structure" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AdminStructure /> : <Navigate to="/calendar" />} />
            <Route path="admin/backup-export" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AdminFinanceExport /> : <Navigate to="/calendar" />} />
            <Route path="admin/anlagen" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AdminAnlagen /> : <Navigate to="/calendar" />} />
            <Route path="admin/logs" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AdminLogs /> : <Navigate to="/calendar" />} />
            <Route path="admin/user-activity" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <UserActivityAnalytics /> : <Navigate to="/calendar" />} />
            <Route path="admin/email-protokoll" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AdminEmailProtokoll /> : <Navigate to="/calendar" />} />
            <Route path="admin/backup-status" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <AdminBackupStatus /> : <Navigate to="/calendar" />} />
            <Route path="admin/privacy" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <PrivacyManagement /> : <Navigate to="/calendar" />} />
            <Route path="admin/paypal-setup" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <PayPalSetupGuide /> : <Navigate to="/calendar" />} />
            <Route path="admin/projects" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <ProjectsManagement /> : <Navigate to="/calendar" />} />
            <Route path="admin/waitlist" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <WaitlistManagement /> : <Navigate to="/calendar" />} />
            <Route path="admin/components-demo" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <ComponentsDemo /> : <Navigate to="/calendar" />} />
            <Route path="admin/events" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <EventManagement /> : <Navigate to="/calendar" />} />

            {/* Kassenprüfer - Zugriff assignment-basiert (Backend erzwingt pro Jahr); Gäste raus */}
            <Route path="kassenpruefer" element={getPrimaryRole(effectiveUser) !== ROLES.GUEST ? <Kassenpruefer /> : <Navigate to="/calendar" />} />

            {/* Veranstaltungen - für Members, Trainer und Vorstand (keine Gäste) */}
            <Route path="veranstaltungen" element={getPrimaryRole(effectiveUser) !== ROLES.GUEST ? <MyEvents /> : <Navigate to="/calendar" />} />

            {/* Routes for all users */}
            <Route path="calendar" element={<Calendar />} />
            <Route path="my-bookings" element={<MyBookings />} />
            <Route path="my-dogs" element={<MyDogs />} />
            <Route path="profile" element={<Profile />} />
            <Route path="my-drinks" element={<MyDrinks />} />

            {/* Vereinsprojekte - nur für Members, Trainer und Vorstand (keine Gäste) */}
            <Route path="my-projects" element={getPrimaryRole(effectiveUser) !== ROLES.GUEST ? <MyProjects /> : <Navigate to="/calendar" />} />
            <Route path="paypal-success" element={<PayPalSuccess />} />
            <Route path="paypal-cancel" element={<PayPalCancel />} />
            
            {/* Guest cards routes */}
            <Route path="guest-cards" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <GuestCardManagement /> : <Navigate to="/calendar" />} />
            <Route path="verzehrkosten" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <VerzehrkostenBeta /> : <Navigate to="/calendar" />} />
            <Route path="my-guest-cards" element={getPrimaryRole(effectiveUser) === ROLES.GUEST ? <MyGuestCards /> : <Navigate to="/calendar" />} />
            
            {/* Statistics route */}
            <Route path="member-statistics" element={getPrimaryRole(effectiveUser) === ROLES.BOARD ? <MemberStatistics /> : <Navigate to="/calendar" />} />
          </Route>
          {/* Catch-all route - redirect to login if not authenticated */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
